
import { State, useComponentState} from '#core/state/State.ts';
import i18n from '#i18n.ts';

// Persisted directly in sessionStorage (NOT via the State storage mechanism):
// useComponentState constructs GuardState with storage=null, so forceStore() is a
// no-op and any State-backed flag would reset every reload — turning the intended
// single retry into an infinite reload loop. A dedicated key survives the reload.
const RELOAD_FLAG = 'swGuard.reloadedOnce';

// Safety valve: if a detected update never takes control (e.g. a waiting worker that
// never activates), fall back to rendering against the current worker rather than
// leaving the user stuck on the "Updating…" screen forever.
const UPDATE_HOLD_TIMEOUT_MS = 10000;

class GuardState extends State<GuardState>{
    // 'n' loading · 'y' ready · 'e' terminal failure · 'a' reinstalling (first install) ·
    // 'u' updating (a new build is taking over; hold until it reloads us)
    loaded:'y'|'n'|'e'|'a'|'u' = 'n';
    constructor(storageName:string|undefined = undefined, storage:Storage|null = sessionStorage){
        super(storageName,storage);
        if(!('serviceWorker' in navigator)){
            this.loaded = 'e';
            return;
        }
        if(navigator.serviceWorker.controller != null)
            this.loaded = 'y'
        navigator.serviceWorker.ready.then((registration)=>{
            if(navigator.serviceWorker.controller == null){
                //controller is null if you bypass-for-network or do a hard reload
                this.reactTo.loaded = 'a';
                if(sessionStorage.getItem(RELOAD_FLAG) !== '1'){
                    //Try to just reload once — the flag persists across the reload
                    sessionStorage.setItem(RELOAD_FLAG, '1');
                    setTimeout(()=>{document.location.reload()},5);
                }else{
                    this.reactTo.loaded = 'e'
                }
                return;
            }
            sessionStorage.removeItem(RELOAD_FLAG);
            // We are controlled by our own worker. Before rendering the app, make the
            // whole SW install/refresh cycle part of the gate: if a newer build is (or
            // becomes) available, hold on an "Updating…" screen instead of rendering and
            // firing authenticated requests against a worker that is about to
            // skipWaiting() → claim() → force a reload. When the new worker takes control
            // the controllerchange handler in registerServiceworker reloads us onto it,
            // and the fresh load — with no pending update — renders normally.
            this.watchForUpdate(registration);
            if(this.loaded !== 'u')
                this.reactTo.loaded='y';
        }).catch(()=>{
            this.reactTo.loaded='e'
        })
    }

    /**
     * Flip the gate to "updating" while a freshly-detected worker finishes installing
     * and takes over. Only engages when a worker already controls the page (a genuine
     * update) — first installs are handled by the reload path above, which must not be
     * turned into an update hold. Watches an already-waiting worker, one currently
     * installing, and any discovered later via `updatefound`.
     */
    private watchForUpdate(registration:ServiceWorkerRegistration){
        if(navigator.serviceWorker.controller == null) return;
        const beginHold = () => {
            if(this.loaded === 'u') return;
            this.reactTo.loaded = 'u';
            // Don't strand the user if the swap never completes.
            setTimeout(()=>{ if(this.loaded === 'u') this.reactTo.loaded = 'y'; }, UPDATE_HOLD_TIMEOUT_MS);
        };
        const track = (worker:ServiceWorker|null) => {
            if(!worker) return;
            const check = () => {
                // Once the new worker has finished downloading (installed) it will
                // activate + claim almost immediately (the SW skips waiting on install),
                // so the reload is imminent — hold now to hide any in-flight error.
                if(worker.state === 'installed' || worker.state === 'activating' || worker.state === 'activated')
                    beginHold();
            };
            check();
            worker.addEventListener('statechange', check);
        };
        track(registration.waiting);
        track(registration.installing);
        registration.addEventListener('updatefound', ()=>track(registration.installing));
    }
}

export function ServiceWorkerGuard({ children }:
  { children: React.ReactNode}) {
    const state= useComponentState(GuardState,['loaded']);
    // This guard renders above the router (and thus outside any Suspense boundary), so
    // we read translations off the i18n instance directly with English fallbacks rather
    // than the suspending `useTranslation` hook.
    const tr = (key: string, fallback: string) => i18n.t(key, { ns: 'general', defaultValue: fallback });
    if(state.loaded == 'y')
        return children;
    else if (state.loaded == 'n')
        return <div className='mt-5 pt-5 text-center text-secondary'>{tr('serviceWorker.loading', 'Loading…')}</div>
    else if (state.loaded == 'a')
        return <div className='mt-5 pt-5 text-center text-secondary'>{tr('serviceWorker.reinstalling', 'Preparing the app…')}</div>
    else if (state.loaded == 'u')
        return <div className='mt-5 pt-5 text-center text-secondary'>{tr('serviceWorker.updating', 'Updating to the latest version…')}</div>
    // Terminal failure: the worker could not be installed even after a reload — almost
    // always a private/incognito window or a browser that blocks service workers / site data.
    else return (
        <div className='mt-5 pt-5 text-center text-secondary mx-auto px-3' style={{ maxWidth: 560 }}>
            {tr('serviceWorker.unsupported', "This app needs a service worker, which your browser or its current settings don't allow. This often happens in private/incognito windows or when site data is blocked. Please open it in a normal window of a supported browser (Chrome, Edge, Firefox or Safari).")}
        </div>
    )
}
